import {
  FeaturedCard,
  LinkFeaturedCardData,
  LinkType,
  PathFeaturedCardData,
} from "@onmo/graphql/graphql";
import { IServiceDataItem } from "../components/common/FeatureSlider/FeatureSlider";
import { servicesList } from "../constants/services";
import {
  MOCK_SERVICE_INTERACTIONS,
  LAUNCHPAD_ICON,
  LaunchpadId,
} from "../constants/servicesOld";
import i18next from "i18next";
import { LandingConfig, Localized, LocalizedAssets } from "../types/home";
import { defaultLandingConfig } from "../constants/pages";

export interface ItemInteraction {
  isFavorited: boolean;
  dateFavorited: string | null;
}
export interface AllInteractions {
  [itemKey: string]: ItemInteraction | undefined;
}

export type VideoData = {
  title: string;
  videoId: string;
  description: string;
  publishTime: string;
  watchUrl: string;
  embedUrl: string;
  thumbnailUrl: {
    src1x: string;
    src2x: string;
    src3x: string;
  };
};

/**
 * Sort list servive, first item(featured slider) in list is kept
 * Other items sorted by favorites descending
 *
 * @param originalList
 * @param allInteractionsData
 * @returns new sorted list service by favorite
 */
const getSortedServices = (
  originalList: ReadonlyArray<IServiceDataItem>,
  allInteractionsData: Readonly<AllInteractions>,
): IServiceDataItem[] => {
  if (!originalList || originalList.length === 0) return [];

  const firstItem = originalList[0];
  const otherItems = [...originalList.slice(1)];

  otherItems.sort((itemA, itemB) => {
    const interactionA = allInteractionsData[itemA.id] || {
      isFavorited: false,
      dateFavorited: null,
    };
    const interactionB = allInteractionsData[itemB.id] || {
      isFavorited: false,
      dateFavorited: null,
    };
    if (interactionA.isFavorited && !interactionB.isFavorited) return -1;
    if (!interactionA.isFavorited && interactionB.isFavorited) return 1;

    if (interactionA.isFavorited && interactionB.isFavorited) {
      const timeA = interactionA.dateFavorited
        ? new Date(interactionA.dateFavorited).getTime()
        : 0;
      const timeB = interactionB.dateFavorited
        ? new Date(interactionB.dateFavorited).getTime()
        : 0;

      if (timeB !== timeA) return timeB - timeA;
    }

    return 0;
  });

  return [firstItem, ...otherItems];
};

/**
 * update new value to local storage if user click favorite/unfavorite
 *
 * @param itemId
 * @returns update list service if user toggle
 */
const toggleFavorite = (itemId: string): AllInteractions => {
  const newAllInteractions: AllInteractions =
    HomepageMd.initializeInteractions();
  const currentItemInteraction = newAllInteractions[itemId] ?? {
    isFavorited: false,
    dateFavorited: null,
  };

  if (currentItemInteraction.isFavorited) {
    newAllInteractions[itemId] = { isFavorited: false, dateFavorited: null };
  } else {
    newAllInteractions[itemId] = {
      isFavorited: true,
      dateFavorited: new Date().toISOString(),
    };
  }
  localStorage.setItem(
    "serviceInteractions",
    JSON.stringify(newAllInteractions),
  );
  return newAllInteractions;
};

const initializeInteractions = (): AllInteractions => {
  const storedInteractionsRaw = localStorage.getItem("serviceInteractions");
  let interactionsToReturn: AllInteractions;

  if (storedInteractionsRaw) {
    try {
      const parsedInteractions = JSON.parse(storedInteractionsRaw);
      interactionsToReturn = parsedInteractions as AllInteractions;
    } catch (error) {
      interactionsToReturn = { ...MOCK_SERVICE_INTERACTIONS };
      localStorage.setItem(
        "serviceInteractions",
        JSON.stringify(interactionsToReturn),
      );
    }
  } else {
    interactionsToReturn = { ...MOCK_SERVICE_INTERACTIONS };
    localStorage.setItem(
      "serviceInteractions",
      JSON.stringify(interactionsToReturn),
    );
  }

  return interactionsToReturn;
};

const isFavoriteService = (serviceId?: string) => {
  const interactions = initializeInteractions();
  const favoriteItem = servicesList.filter(
    (item) => interactions[item.id]?.isFavorited || false,
  );

  return !!favoriteItem.find((item) => item.id === serviceId);
};

const getGameBySelected = (
  serviceData: IServiceDataItem,
  selectedService: string,
) => {
  const items = serviceData.sliderItems ?? [];
  if (!selectedService) return items;
  return items.filter((it: any) =>
    (it.partner || []).some((p: string) => p.toLowerCase() === selectedService),
  );
};

function getLaunchpadIcons(launchpads?: string[]) {
  if (!launchpads || launchpads.length === 0) return [];
  const ids = Array.from(
    new Set(launchpads.filter((x): x is LaunchpadId => x !== null)),
  );
  return ids.map((id) => ({
    id,
    src: LAUNCHPAD_ICON[id],
  }));
}

const getLink = (gameFeed: FeaturedCard) => {
  switch (gameFeed.linkType) {
    case LinkType.InternalLink:
      return (gameFeed.data as PathFeaturedCardData).internalPath;
    case LinkType.ExternalIframe:
      return (gameFeed.data as LinkFeaturedCardData).redirectUrl;
    default:
      return "";
  }
};

export function getLocalized<T>(
  localizedData: Localized<T> | null | undefined,
  fallbackLanguage = "en",
): T | null {
  if (!localizedData) return null;

  const currentLanguage = i18next.language;
  const primaryLanguage = currentLanguage?.split("-")[0];

  return (
    localizedData[currentLanguage] ??
    localizedData[primaryLanguage] ??
    localizedData[fallbackLanguage] ??
    null
  );
}

export function getLocalizedAssets(
  assets: Record<string, LocalizedAssets> | null | undefined,
): LocalizedAssets | null {
  return getLocalized<LocalizedAssets>(assets);
}

export const getLandingConfig = (): Required<LandingConfig> => {
  const config = import.meta.env.VITE_LANDING_CONFIG;
  if (!config) return defaultLandingConfig;

  try {
    const overrides = JSON.parse(config) as LandingConfig;
    if (!overrides || Array.isArray(overrides) || typeof overrides !== "object")
      return defaultLandingConfig;

    return {
      ...defaultLandingConfig,
      ...overrides,
      button: {
        ...defaultLandingConfig.button,
        ...overrides.button,
      },
    };
  } catch {
    return defaultLandingConfig;
  }
};

export const HomepageMd = {
  getSortedServices,
  toggleFavorite,
  initializeInteractions,
  isFavoriteService,
  getGameBySelected,
  getLaunchpadIcons,
  getLink,
  getLocalized,
  getLocalizedAssets,
};
